/**
 * Reconciliation lifecycles: single source of truth for finance items that need manual review.
 *
 * Each lifecycle has ordered steps; steps with needs_manual_review = true are the ones
 * that require human action. The briefing shows items grouped by these lifecycles.
 *
 * Lifecycles are either 'linear' (payouts: sequential steps, rendered as a flowchart)
 * or 'branching' (bank statements: items can take different paths, rendered as grouped statuses).
 */

export interface LifecycleStep {
  id: string;
  label: string;
  needs_manual_review: boolean;
}

export interface ReconciliationLifecycle {
  id: string;
  label: string;
  link: string;
  /** 'linear' = sequential steps (flowchart), 'branching' = items take different paths (group-by-status). */
  type: 'linear' | 'branching';
  steps: LifecycleStep[];
  /** Human-readable action hint per status, shown in the grouped status view for branching lifecycles. */
  statusActions?: Record<string, string>;
  talkToAIPrompt: string;
}

/** Payout: from pending through send/approve to terminal. Steps needing manual review = user must Send or Approve. */
export const PAYOUT_LIFECYCLE: ReconciliationLifecycle = {
  id: 'payouts',
  label: 'Payouts',
  link: '/app/finances/payouts',
  type: 'linear',
  steps: [
    { id: 'pending', label: 'Pending', needs_manual_review: true },
    { id: 'send_requested', label: 'Send requested', needs_manual_review: false },
    { id: 'pending_atlar_approval', label: 'Pending Atlar approval', needs_manual_review: true },
    { id: 'approve_requested', label: 'Approve requested', needs_manual_review: false },
    { id: 'accepted', label: 'Accepted', needs_manual_review: false },
    { id: 'sent', label: 'Sent', needs_manual_review: false },
    { id: 'confirmed', label: 'Confirmed', needs_manual_review: false },
  ],
  talkToAIPrompt:
    'Walk me through the payout lifecycle. Which of these items need my action (Send or Approve), and what do I do next to complete each one?',
};

/** Bank statement line (non-PSP): classify then match. Branching — items can go to different terminal states. */
export const BANK_STATEMENTS_LIFECYCLE: ReconciliationLifecycle = {
  id: 'bank_statements',
  label: 'Bank statements',
  link: '/app/finances/bank-statements',
  type: 'branching',
  steps: [
    { id: 'pending', label: 'Pending classification', needs_manual_review: true },
    { id: 'classified', label: 'Classified', needs_manual_review: true },
    { id: 'partially_matched', label: 'Partially matched', needs_manual_review: true },
    { id: 'disputed', label: 'Disputed', needs_manual_review: true },
    { id: 'auto_matched', label: 'Auto matched', needs_manual_review: false },
    { id: 'manual_matched', label: 'Manual matched', needs_manual_review: false },
  ],
  statusActions: {
    pending: 'Classify these bank lines',
    classified: 'Match to a case, transaction, or reconcile payout',
    partially_matched: 'Complete the remaining match',
    disputed: 'Classify these bank lines',
  },
  talkToAIPrompt:
    'Explain the reconciliation lifecycle for bank statement lines. What step are my items at, and what do I need to do to take them from start to end of the lifecycle?',
};

/** PSP receipt: match bank line to settlements then confirm. Only pending_review needs manual action. */
export const PSP_MATCHING_LIFECYCLE: ReconciliationLifecycle = {
  id: 'psp_matching',
  label: 'PSP Matching',
  link: '/app/finances/psp-matching',
  type: 'branching',
  steps: [
    { id: 'pending_review', label: 'Pending review', needs_manual_review: true },
    { id: 'auto_matched', label: 'Matched', needs_manual_review: false },
    { id: 'manual_matched', label: 'Matched', needs_manual_review: false },
  ],
  statusActions: {
    pending_review: 'Review and confirm the PSP match',
  },
  talkToAIPrompt:
    'Explain the reconciliation lifecycle for a PSP receipt. What do I need to do in PSP Matching to match and confirm these items?',
};

export const RECONCILIATION_LIFECYCLES: ReconciliationLifecycle[] = [PAYOUT_LIFECYCLE, BANK_STATEMENTS_LIFECYCLE, PSP_MATCHING_LIFECYCLE];

export function getLifecycleById(id: string): ReconciliationLifecycle | undefined {
  return RECONCILIATION_LIFECYCLES.find((lc) => lc.id === id);
}

export function getStepsNeedingManualReview(lifecycleId: string): string[] {
  const lc = getLifecycleById(lifecycleId);
  return lc ? lc.steps.filter((s) => s.needs_manual_review).map((s) => s.id) : [];
}

export function getTalkToAIPrompt(lifecycleId: string): string | null {
  return getLifecycleById(lifecycleId)?.talkToAIPrompt ?? null;
}
